/**
 * GLAMRDiP shared infinite-scroll module — collection grids.
 * Joel 2026-05-02.
 *
 * Progressive enhancement on top of the server-side `{% paginate %}`
 * pagination. Server still renders page N for any direct ?page=N
 * request (SEO + no-JS users). On a JS-capable client, this module
 * replaces "click Load more / numbered links" with auto-fetch on
 * scroll-into-view.
 *
 * Single-source-of-truth for collection-grid pagination. Used by
 * snippets/gd-product-grid.liquid → consumed by colours-grid /
 * liquids-grid / accessories-grid sections → reaches every collection
 * template (collection.json + collection.colours.json +
 * collection.liquids.json + collection.accessories.json).
 *
 * Markup contract (server-rendered by gd-product-grid.liquid):
 *   <section data-gd-infinite-root data-gd-infinite-next="<NEXT_URL>">
 *     <div data-gd-infinite-grid>...product cards...</div>
 *     <div data-gd-infinite-sentinel>
 *       <a href="<NEXT_URL>" data-gd-infinite-button>Load more products</a>
 *       <span data-gd-infinite-spinner aria-hidden="true">...</span>
 *     </div>
 *     <div class="gd-sr-only" aria-live="polite" data-gd-infinite-live></div>
 *   </section>
 *
 * Behaviour:
 *   - rootMargin 300px so the next page starts loading just before the
 *     user reaches the bottom (eliminates the "wait for products" jank).
 *   - inflight flag dedupes — a fast scroll won't fire 5 fetches.
 *   - On each successful fetch: appends new product cards into the
 *     grid, reads the next page's `data-gd-infinite-next` from the
 *     fetched HTML, advances state. When that's empty → end-of-results.
 *   - history.replaceState updates ?page=N in the URL so refresh +
 *     copy/share lands the user on the same depth.
 *   - aria-live region announces "12 more products loaded" when each
 *     batch appends; "All products loaded" on end-of-results.
 *   - Click on the Load more <a> falls back to a regular fetch (same
 *     code path) — keyboard-accessible. With JS off the <a> is a
 *     regular link to ?page=2 (server-rendered).
 *   - Idempotent via dataset.gdInfiniteInit so shopify:section:load
 *     re-init doesn't double-bind.
 */
(() => {
  'use strict';

  const init = (root) => {
    if (!root || root.dataset.gdInfiniteInit === '1') return;
    root.dataset.gdInfiniteInit = '1';

    const grid       = root.querySelector('[data-gd-infinite-grid]');
    const sentinel   = root.querySelector('[data-gd-infinite-sentinel]');
    const liveRegion = root.querySelector('[data-gd-infinite-live]');
    const button     = root.querySelector('[data-gd-infinite-button]');

    if (!grid) return;

    let nextUrl = (root.dataset.gdInfiniteNext || '').trim();
    let inflight = false;
    let ended = !nextUrl;

    if (ended) {
      // No next page from the server-rendered first paint — nothing to do.
      sentinel?.setAttribute('hidden', '');
      return;
    }

    const announce = (text) => {
      if (liveRegion) liveRegion.textContent = text;
    };

    const setLoading = (on) => {
      if (sentinel) sentinel.classList.toggle('is-loading', on);
      if (button) button.setAttribute('aria-busy', on ? 'true' : 'false');
    };

    const updateBrowserUrl = (loadedUrl) => {
      // The fetched request is the page just appended, including the final
      // page where no next-page URL remains.
      try {
        const current = new URL(window.location.href);
        const fetched = new URL(loadedUrl, window.location.href);
        const loaded = Math.max(1, parseInt(fetched.searchParams.get('page'), 10) || 1);
        if (loaded > 1) current.searchParams.set('page', String(loaded));
        else current.searchParams.delete('page');
        history.replaceState(null, '', current.toString());
      } catch (_) { /* ignore */ }
    };

    const finishEnded = () => {
      ended = true;
      sentinel?.setAttribute('hidden', '');
      announce('All products loaded.');
    };

    const loadNext = async () => {
      if (inflight || ended || !nextUrl) return;
      inflight = true;
      setLoading(true);

      try {
        const loadedUrl = nextUrl;
        const res = await fetch(loadedUrl, { headers: { 'Accept': 'text/html' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newGrid = doc.querySelector('[data-gd-infinite-grid]');
        const newRoot = doc.querySelector('[data-gd-infinite-root]');
        if (!newGrid || !newRoot) throw new Error('infinite-scroll markup missing in fetched page');

        // Append new product cards to the live grid.
        const incoming = Array.from(newGrid.children);
        const isProductCard = (card) => card.classList.contains('gd-colour-card');
        const productGrid = grid.classList.contains('gd-colours__grid') ||
          Array.from(grid.children).some(isProductCard) || incoming.some(isProductCard);
        const added = productGrid ? incoming.filter(isProductCard).length : incoming.length;
        const fragment = document.createDocumentFragment();
        incoming.forEach((card) => fragment.appendChild(card));
        grid.appendChild(fragment);

        // Advance pagination state from the fetched root.
        const newNext = (newRoot.dataset.gdInfiniteNext || '').trim();
        if (newNext) {
          nextUrl = newNext;
          root.dataset.gdInfiniteNext = newNext;
          if (button) button.setAttribute('href', newNext);
          updateBrowserUrl(loadedUrl);
          announce(`${added} more product${added === 1 ? '' : 's'} loaded.`);
        } else {
          // Last page — there's no further "next" link.
          // The current URL still reflects the page we just loaded;
          // update it to that page number rather than removing ?page.
          updateBrowserUrl(loadedUrl);
          finishEnded();
        }
      } catch (e) {
        console.warn('[gd-infinite-scroll] fetch failed:', e);
        // Don't disable the button; user can retry by clicking it.
      } finally {
        inflight = false;
        setLoading(false);
      }
    };

    // IntersectionObserver primary trigger.
    if ('IntersectionObserver' in window && sentinel) {
      const io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) loadNext();
      }, { rootMargin: '300px 0px' });
      io.observe(sentinel);
    }

    // Click fallback / keyboard activation on the Load more <a>.
    // Prevents the default navigation; we want the AJAX append path
    // when JS is alive. With JS off the <a> behaves as a regular link.
    if (button) {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        loadNext();
      });
    }
  };

  const initAll = () => {
    document.querySelectorAll('[data-gd-infinite-root]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
  // Re-init on theme-editor section reload (customizer drag/drop).
  document.addEventListener('shopify:section:load', initAll);
})();

